import React, { useState } from 'react';
import {
    DataTable,
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableCell,
    TableContainer,
    TableToolbar,
    TableToolbarContent,
    TableToolbarSearch,
    Pagination,
    OverflowMenu,
    OverflowMenuItem, 
    Button
} from '@carbon/react';
import { Edit, TrashCan } from '@carbon/icons-react';

const SearchTable = ({ 
    title, 
    rows, 
    headers, 
    onEditRow, 
    actionButtonText, 
    onActionClick,
    downloadFileName = 'download.csv', // NUEVO PROP CON VALOR POR DEFECTO
    ...args 
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    const convertToCSV = (data) => {
        if (data.length === 0) {
            return '';
        }

        // USAR LOS HEADERS QUE SE PASAN COMO PROP
        // Crear el mapeo dinámicamente desde los headers
        const headerMapping = {};
        headers.forEach(header => {
            headerMapping[header.key] = header.header;
        });

        const csvHeaders = Object.values(headerMapping).join(',') + '\n';
        
        const csvRows = data.map(row => {
            return Object.keys(headerMapping).map(key => {
                let value = row[key];
                
                // Handle null or undefined values
                if (value === null || value === undefined) {
                    value = '';
                } else if (!isNaN(value) && typeof value === 'number' && value.toString().includes('.')) {
                    // If it's a number with a dot, replace the dot with a comma
                    value = value.toString().replace('.', ',');
                }
                
                return `"${value}"`;
            }).join(',');
        }).join('\n');

        return csvHeaders + csvRows;
    };

    const downloadCSV = (csvContent, fileName) => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownload = () => {
        const csv = convertToCSV(rows);
        downloadCSV(csv, downloadFileName); // USAR EL PROP
    };

    return (
        <DataTable
            rows={rows}
            headers={headers}
            {...args}
            isSortable
        >
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getTableProps,
                getToolbarProps,
                onInputChange,
                getTableContainerProps,
            }) => (
                <TableContainer title={title} {...getTableContainerProps()}>
                    <TableToolbar {...getToolbarProps()} aria-label="barra de herramientas de la tabla">
                        <TableToolbarContent>
                            <TableToolbarSearch placeholder="Buscar" onChange={onInputChange} />
                            <Button onClick={handleDownload}>Descargar</Button>
                        </TableToolbarContent>
                    </TableToolbar>
                    <Table {...getTableProps()} aria-label="tabla">
                        <TableHead>
                            <TableRow>
                                {headers.map(header => (
                                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                                        {header.header}
                                    </TableHeader>
                                ))}
                                {(onEditRow || onActionClick) && <TableHeader></TableHeader>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.slice((page - 1) * pageSize, page * pageSize).map(row => (
                                <TableRow key={row.id} {...getRowProps({ row })}>
                                    {row.cells.map(cell => (
                                        <TableCell key={cell.id}>{cell.value}</TableCell>
                                    ))}
                                    {(onEditRow || onActionClick) && (
                                        <TableCell className="actions-cell">
                                            <OverflowMenu
                                                flipped
                                                tooltipAlignment="center"
                                                iconDescription="Acciones"
                                                ariaLabel="Menú de acciones"
                                            >
                                                <OverflowMenuItem
                                                    itemText={actionButtonText || "Editar"}
                                                    onClick={() => {
                                                        if (onActionClick) {
                                                            onActionClick(row);
                                                        } else if (onEditRow) {
                                                            onEditRow(row.id);
                                                        }
                                                    }}
                                                    renderIcon={Edit}
                                                />
                                            </OverflowMenu>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Pagination
                        totalItems={rows.length}
                        pageSizes={[5, 10, 15]}
                        itemsPerPageText="Elementos por página"
                        pageRangeText={(current, total) => `${current}–${total} página(s)`}
                        itemRangeText={(min, max) => `${min}–${max} Filas`}
                        pageNumberText="Número de página"
                        page={page}
                        pageSize={pageSize}
                        onChange={({ page, pageSize }) => {
                            setPage(page);
                            setPageSize(pageSize);
                        }}
                        forwardText="Página siguiente"
                        backwardText="Página anterior"
                    />
                </TableContainer>
            )}
        </DataTable>
    );
};

export default SearchTable;